import { useState } from 'react';
import { useFormConfigStore } from '../state/store';
import { classifyField } from '@capture/shared';
import type { Behavior, FieldContent } from '@capture/shared';
import { HeaderFieldRow } from './HeaderFieldRow';
import { HeaderFieldComposer } from './HeaderFieldComposer';
import './HeaderFieldsPane.css';

interface Props {
  format: 'xml' | 'json';
  lastClickedPath?: string | null;
  onComposerClose?: () => void;
}

/**
 * Lists every field classified as `'header'` (via shared `classifyField`).
 * Each row exposes an inline path edit, required toggle, and remove button
 * that mutate the active draft via the form-config store.
 *
 * Phase 1: also hosts the HeaderFieldComposer, opened either by a tree-click
 * (lastClickedPath prop) or by the manual "+ Add header field" button.
 */
export function HeaderFieldsPane({ format, lastClickedPath, onComposerClose }: Props) {
  const content = useFormConfigStore((s) => s.content);
  const updateField = useFormConfigStore((s) => s.updateField);
  const deleteField = useFormConfigStore((s) => s.deleteField);
  const [manualOpen, setManualOpen] = useState(false);

  if (!content) return null;
  const headerFields = content.fields.filter((f) => classifyField(f) === 'header');

  const composerPath = lastClickedPath ?? (manualOpen ? '' : null);
  const composerOpen = composerPath !== null;

  function closeComposer() {
    setManualOpen(false);
    onComposerClose?.();
  }

  if (headerFields.length === 0 && !composerOpen) {
    return (
      <div className="header-fields-pane header-fields-pane--empty">
        <h3 className="header-fields-pane__empty-title">No header field mappings yet.</h3>
        <p className="header-fields-pane__empty-body">
          Paste a sample header on the left, then click any node to map it as a
          batch-scope field. These values get captured per batch and become available
          to downstream features that consume batch context (export branching,
          document defaults, reconciliation, downstream products).
        </p>
        <button
          type="button"
          className="header-fields-pane__add-btn"
          onClick={() => setManualOpen(true)}
        >
          + Add header field
        </button>
      </div>
    );
  }

  return (
    <div className="header-fields-pane">
      <div className="header-fields-pane__header">
        <h3 className="header-fields-pane__title">Header field mappings</h3>
        <button
          type="button"
          className="header-fields-pane__add-btn"
          onClick={() => setManualOpen(true)}
        >
          + Add header field
        </button>
      </div>
      {composerOpen && (
        <HeaderFieldComposer
          format={format}
          path={composerPath!}
          onAdded={closeComposer}
          onCancel={closeComposer}
        />
      )}
      {headerFields.map((f) => (
        <HeaderFieldRow
          key={f.fieldDefinitionId}
          field={f}
          format={format}
          onPathChange={(nextPath) =>
            updateField(f.name, { stageMap: updatePathInStageMap(f, format, nextPath) })
          }
          onRequiredChange={(nextRequired) =>
            updateField(f.name, {
              stageMap: updateRequiredInStageMap(f, nextRequired),
            })
          }
          onRemove={() => deleteField(f.name)}
        />
      ))}
    </div>
  );
}

function updatePathInStageMap(
  field: FieldContent,
  format: 'xml' | 'json',
  nextPath: string,
): FieldContent['stageMap'] {
  const imp: Behavior[] = [...(field.stageMap.import ?? [])];
  const first = imp[0];
  if (first && first.kind === 'populate') {
    const src = first.source;
    if (format === 'xml' && src.type === 'header-xpath') {
      imp[0] = { ...first, source: { ...src, xpath: nextPath } };
    } else if (format === 'json' && src.type === 'header-path') {
      imp[0] = { ...first, source: { ...src, path: nextPath } };
    }
  }
  return { ...field.stageMap, import: imp };
}

function updateRequiredInStageMap(
  field: FieldContent,
  nextRequired: boolean,
): FieldContent['stageMap'] {
  const imp: Behavior[] = [...(field.stageMap.import ?? [])];
  const first = imp[0];
  if (first && first.kind === 'populate') {
    const src = first.source;
    if (src.type === 'header-xpath' || src.type === 'header-path') {
      imp[0] = { ...first, source: { ...src, required: nextRequired } };
    }
  }
  return { ...field.stageMap, import: imp };
}
